import { useEffect, useRef, useState } from "react";
import {
  Building2,
  Lock,
  ShieldCheck,
  Check,
  Loader2,
  ArrowRight,
  CreditCard,
  Wallet,
  Landmark,
} from "lucide-react";

/**
 * BankConnectDemo
 * -------------------------------------------------------------
 * An auto-cycling, on-canvas mockup of the Teller "Connect bank"
 * flow used inside the Financials Ready product. Lives in the hero
 * picture frame on the landing page. No real API calls — pure UI.
 */

const BANKS = [
  { id: "chase", name: "Chase", color: "#117ACA" },
  { id: "bofa", name: "Bank of America", color: "#E31837" },
  { id: "wells", name: "Wells Fargo", color: "#D71E28" },
  { id: "citi", name: "Citibank", color: "#003B70" },
  { id: "capone", name: "Capital One", color: "#004977" },
  { id: "amex", name: "American Express", color: "#016FD0" },
];

const ACCOUNTS = [
  { id: "a1", name: "Business Checking ••4821", balance: "$184,302.18", type: "Checking", icon: Wallet },
  { id: "a2", name: "Operating Savings ••9034", balance: "$522,910.55", type: "Savings", icon: Landmark },
  { id: "a3", name: "AmEx Platinum ••1003", balance: "−$8,412.77", type: "Credit", icon: CreditCard },
];

// Scene timing (ms) — total loop ~12.5s
const SCENES = [
  { id: 0, label: "Tap connect", ms: 1800 },
  { id: 1, label: "Pick your bank", ms: 2600 },
  { id: 2, label: "Secure handshake", ms: 2400 },
  { id: 3, label: "Select accounts", ms: 2800 },
  { id: 4, label: "Synced", ms: 2900 },
];

export default function BankConnectDemo() {
  const [scene, setScene] = useState(0);
  const [hoverIdx, setHoverIdx] = useState(0);
  const timerRef = useRef(null);

  // Auto-advance through scenes
  useEffect(() => {
    timerRef.current = setTimeout(() => {
      setScene((s) => (s + 1) % SCENES.length);
    }, SCENES[scene].ms);
    return () => clearTimeout(timerRef.current);
  }, [scene]);

  // Cycle the "selected" bank tile during scene 1
  useEffect(() => {
    if (scene !== 1) return;
    const t = setInterval(() => setHoverIdx((i) => (i + 1) % BANKS.length), 380);
    return () => clearInterval(t);
  }, [scene]);

  const restart = () => {
    clearTimeout(timerRef.current);
    setScene(0);
  };

  return (
    <div
      className="relative ll-grain overflow-hidden border"
      style={{ borderColor: "var(--ll-ink)", background: "#0b5d9e" }}
      data-testid="hero-bank-connect-demo"
    >
      {/* Mock browser / app chrome */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-[#0B2241]">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
        </div>
        <div className="flex-1 mx-3 text-[10px] font-mono-fin tracking-wider text-white/50 truncate">
          financialsready.app/bank-accounts/connect
        </div>
        <Lock size={12} className="text-white/40" />
      </div>

      {/* Scene viewport */}
      <div className="relative h-[412px] overflow-hidden">
        {/* Scene 0 — Connect CTA */}
        <Scene active={scene === 0}>
          <div className="h-full flex flex-col items-center justify-center px-8 text-center">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">
              Step 1 of 3
            </div>
            <div className="font-display font-bold text-2xl text-white leading-tight max-w-[280px]">
              Securely link your bank in under 30 seconds.
            </div>
            <p className="text-xs text-white/60 mt-3 max-w-[280px] leading-relaxed">
              Read-only, bank-grade MTLS. We never see your password.
            </p>
            <button
              type="button"
              className="mt-7 group relative px-6 py-3 bg-white text-[#0F172A] font-medium text-sm flex items-center gap-2 overflow-hidden"
              data-testid="bank-demo-connect-btn"
            >
              <span className="relative z-10 flex items-center gap-2">
                <Building2 size={16} /> Connect bank
                <ArrowRight size={14} />
              </span>
              {/* Pulse ring */}
              <span className="absolute inset-0 ring-2 ring-white/60 animate-[pulseRing_1.4s_ease-out_infinite]" />
            </button>
            <div className="mt-8 flex items-center gap-2 text-[10px] text-white/40 uppercase tracking-[0.25em]">
              <ShieldCheck size={12} /> Powered by Teller
            </div>
          </div>
        </Scene>

        {/* Scene 1 — Bank picker */}
        <Scene active={scene === 1}>
          <div className="h-full flex flex-col px-6 py-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-1">
              Step 2 of 3
            </div>
            <div className="font-display font-bold text-white text-xl mb-1">
              Choose your bank
            </div>
            <div className="text-xs text-white/50 mb-5">
              12,000+ US institutions supported
            </div>
            <div className="grid grid-cols-2 gap-2 flex-1">
              {BANKS.map((b, i) => {
                const isHot = i === hoverIdx;
                return (
                  <div
                    key={b.id}
                    className={`relative px-3 py-3 border bg-white/[0.04] flex items-center gap-2.5 transition-all duration-300 ${
                      isHot
                        ? "border-white scale-[1.02] bg-white/[0.12]"
                        : "border-white/10"
                    }`}
                  >
                    <span
                      className="w-7 h-7 grid place-items-center text-white text-[11px] font-bold shrink-0"
                      style={{ background: b.color }}
                    >
                      {b.name.charAt(0)}
                    </span>
                    <span className="text-xs text-white font-medium truncate">
                      {b.name}
                    </span>
                    {isHot && (
                      <span className="absolute -right-px -top-px w-1.5 h-1.5 bg-[#0f172a] animate-ping" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </Scene>

        {/* Scene 2 — Secure handshake */}
        <Scene active={scene === 2}>
          <div className="h-full flex flex-col items-center justify-center px-8 text-center">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-6">
              MTLS handshake
            </div>
            <div className="relative w-full max-w-[280px] h-24 flex items-center justify-between">
              {/* Financials Ready node */}
              <div className="relative z-10 w-16 h-16 grid place-items-center bg-white text-[#0F172A]">
                <span className="font-display font-bold text-2xl">L</span>
              </div>
              {/* Wire */}
              <div className="absolute inset-x-16 top-1/2 -translate-y-1/2 h-px bg-white/20 overflow-hidden">
                <div className="h-full w-1/3 bg-[#0f172a] animate-[wirePulse_1.4s_linear_infinite]" />
              </div>
              {/* Lock badge */}
              <div className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-1/2 z-20 w-9 h-9 grid place-items-center bg-[#0F172A] border border-white/30">
                <Lock size={14} className="text-white" />
              </div>
              {/* Bank node */}
              <div
                className="relative z-10 w-16 h-16 grid place-items-center text-white"
                style={{ background: "#117ACA" }}
              >
                <Building2 size={22} />
              </div>
            </div>
            <div className="mt-7 space-y-1.5 text-[11px] text-white/60 font-mono-fin">
              <HandshakeLine delay={0}>TLS 1.3 negotiated</HandshakeLine>
              <HandshakeLine delay={500}>Client certificate verified</HandshakeLine>
              <HandshakeLine delay={1100}>Read-only scope authorized</HandshakeLine>
            </div>
          </div>
        </Scene>

        {/* Scene 3 — Select accounts */}
        <Scene active={scene === 3}>
          <div className="h-full flex flex-col px-6 py-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">
                  Step 3 of 3
                </div>
                <div className="font-display font-bold text-white text-xl">
                  Select accounts
                </div>
              </div>
              <span
                className="w-7 h-7 grid place-items-center text-white text-[11px] font-bold"
                style={{ background: "#117ACA" }}
              >
                C
              </span>
            </div>
            <div className="space-y-2 flex-1">
              {ACCOUNTS.map((a, i) => {
                const Icon = a.icon;
                return (
                  <div
                    key={a.id}
                    className="opacity-0 animate-[slideUp_0.5s_ease-out_forwards] flex items-center gap-3 px-3 py-3 border border-white/10 bg-white/[0.04]"
                    style={{ animationDelay: `${i * 280}ms` }}
                  >
                    <div className="w-8 h-8 grid place-items-center bg-white/10 text-white shrink-0">
                      <Icon size={15} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-white font-medium truncate">
                        {a.name}
                      </div>
                      <div className="text-[10px] text-white/40 uppercase tracking-wider">
                        {a.type}
                      </div>
                    </div>
                    <div className="text-xs font-mono-fin text-white">
                      {a.balance}
                    </div>
                    <span className="w-4 h-4 grid place-items-center bg-[#0f172a] text-white shrink-0">
                      <Check size={11} strokeWidth={3} />
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 text-[10px] text-white/40 flex items-center gap-1.5">
              <Loader2 size={11} className="animate-spin" />
              Importing 60 months of history…
            </div>
          </div>
        </Scene>

        {/* Scene 4 — Success / synced */}
        <Scene active={scene === 4}>
          <div className="h-full flex flex-col px-6 py-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-7 h-7 grid place-items-center bg-[#059669] text-white animate-[popIn_0.35s_ease-out]">
                <Check size={15} strokeWidth={3} />
              </span>
              <div>
                <div className="font-display font-bold text-white text-lg leading-tight">
                  3 accounts connected
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/50">
                  Live · syncing nightly
                </div>
              </div>
            </div>
            <div className="border border-white/10 bg-white/[0.04] p-4 flex-1 flex flex-col">
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-[10px] uppercase tracking-[0.25em] text-white/50">
                  Combined balance
                </span>
                <span className="text-[10px] text-[#34d399]">▲ 4.2% MoM</span>
              </div>
              <div className="font-mono-fin text-white text-3xl">
                <CountUp from={0} to={698800} prefix="$" />
                <span className="text-white/40">.96</span>
              </div>
              {/* Sparkline */}
              <svg
                viewBox="0 0 200 50"
                className="mt-4 w-full h-12"
                preserveAspectRatio="none"
              >
                <defs>
                  <linearGradient id="bcSpark" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#0f172a" stopOpacity="0.45" />
                    <stop offset="100%" stopColor="#0f172a" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path
                  d="M0,38 L20,32 L40,34 L60,26 L80,28 L100,20 L120,22 L140,14 L160,18 L180,10 L200,12 L200,50 L0,50 Z"
                  fill="url(#bcSpark)"
                />
                <path
                  d="M0,38 L20,32 L40,34 L60,26 L80,28 L100,20 L120,22 L140,14 L160,18 L180,10 L200,12"
                  stroke="#60a5fa"
                  strokeWidth="1.5"
                  fill="none"
                  strokeDasharray="400"
                  strokeDashoffset="400"
                  style={{
                    animation: "drawLine 1.2s ease-out 0.2s forwards",
                  }}
                />
              </svg>
              <div className="mt-auto pt-3 grid grid-cols-3 gap-2 text-center">
                {[
                  ["3", "Accounts"],
                  ["847", "Txns"],
                  ["12mo", "History"],
                ].map(([v, k]) => (
                  <div key={k} className="border border-white/10 py-2">
                    <div className="font-mono-fin text-white text-base">{v}</div>
                    <div className="text-[9px] uppercase tracking-widest text-white/40 mt-0.5">
                      {k}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Scene>
      </div>

      {/* Footer / progress strip */}
      <div className="border-t border-white/10 bg-[#0B2241] px-4 py-3 flex items-center gap-3">
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 whitespace-nowrap">
          Bank Connect · Live demo
        </div>
        <div className="flex-1 flex items-center gap-1.5">
          {SCENES.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onClick={() => {
                clearTimeout(timerRef.current);
                setScene(i);
              }}
              aria-label={`Jump to ${s.label}`}
              data-testid={`bank-demo-scene-dot-${i}`}
              className={`h-1 flex-1 transition-colors ${
                i === scene
                  ? "bg-white"
                  : i < scene
                  ? "bg-white/40"
                  : "bg-white/15 hover:bg-white/30"
              }`}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={restart}
          className="text-[10px] uppercase tracking-[0.25em] text-white/60 hover:text-white"
          data-testid="bank-demo-replay"
        >
          Replay
        </button>
      </div>

      {/* Local keyframes (scoped via inline <style>) */}
      <style>{`
        @keyframes fadeScene {
          0% { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulseRing {
          0% { opacity: 0.9; transform: scale(1); }
          100% { opacity: 0; transform: scale(1.18); }
        }
        @keyframes wirePulse {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
        @keyframes slideUp {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes popIn {
          0% { opacity: 0; transform: scale(0.6); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes drawLine {
          to { stroke-dashoffset: 0; }
        }
        @keyframes hsLine {
          0% { opacity: 0; transform: translateX(-6px); }
          100% { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}

function Scene({ active, children }) {
  if (!active) return null;
  return (
    <div
      className="absolute inset-0"
      style={{ animation: "fadeScene 0.5s ease-out" }}
    >
      {children}
    </div>
  );
}

function HandshakeLine({ delay = 0, children }) {
  return (
    <div
      className="flex items-center gap-2 opacity-0"
      style={{
        animation: `hsLine 0.45s ease-out ${delay}ms forwards`,
      }}
    >
      <Check size={11} className="text-[#34d399]" strokeWidth={3} />
      <span>{children}</span>
    </div>
  );
}

function CountUp({ from = 0, to = 100, prefix = "", duration = 1100 }) {
  const [val, setVal] = useState(from);
  useEffect(() => {
    const start = performance.now();
    let frame;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(from + (to - from) * eased));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <span>
      {prefix}
      {val.toLocaleString()}
    </span>
  );
}
